import React from 'react';
import { Layer, Line, Text, Circle, Group } from 'react-konva';
import { useSelector } from 'react-redux';
import {
    analyzeNavigationConnections,
    getConnectionColor,
    getConnectionStyle,
} from './utils/navigationAnalyzer';
import {
    getDeviceDimensions,
    DEVICE_DEFINITIONS,
    DEFAULT_DEVICE,
} from './deviceDefinitions';
import { RootState } from './utils/experienceStore';

interface ConnectionLayerProps {
    screens: ScreenDefinition[];
    screenPositions: { [screenId: string]: { x: number; y: number } };
    showConnections?: boolean;
}

const CANVAS_SCALE = 0.25; // Same scale factor as ScreenCanvasNode

export default function ConnectionLayer({
    screens,
    screenPositions,
    showConnections = true,
}: ConnectionLayerProps) {
    // Get device configuration from Redux
    const { experienceDevices, activeTab } = useSelector(
        (state: RootState) => state.canvas,
    );

    // Calculate device-aware screen dimensions (same as ScreenCanvasNode)
    const currentDevice =
        experienceDevices[activeTab] || experienceDevices.main;
    const device =
        DEVICE_DEFINITIONS.find((d) => d.id === currentDevice.deviceId) ||
        DEFAULT_DEVICE;
    const deviceDimensions = getDeviceDimensions(
        device,
        currentDevice.orientation,
    );
    const SCREEN_WIDTH = deviceDimensions.width * CANVAS_SCALE;
    const SCREEN_HEIGHT = deviceDimensions.height * CANVAS_SCALE;

    // Analyze navigation connections from screen definitions
    const connections = React.useMemo(() => {
        console.log(
            'ConnectionLayer: Analyzing connections for screens:',
            screens.length,
        );
        const result = analyzeNavigationConnections(screens);
        console.log(
            'ConnectionLayer: Found connections:',
            result.length,
            result,
        );
        return result;
    }, [screens]);

    if (!showConnections) {
        return <Layer />;
    }

    // Detect bidirectional connections to prevent overlaps
    const getBidirectionalOffset = (connection: any, allConnections: any[]) => {
        const reverseConnection = allConnections.find(
            (conn) =>
                conn.fromScreenId === connection.toScreenId &&
                conn.toScreenId === connection.fromScreenId,
        );

        if (reverseConnection) {
            // This is a bidirectional connection pair
            // Use connection ID to determine which one gets offset
            return connection.id < reverseConnection.id ? 15 : -15; // Offset by 15px
        }
        return 0;
    };

    // Calculate connection points between screens
    const calculateConnectionPoints = (
        fromScreenId: string,
        toScreenId: string,
        connection: any,
    ) => {
        const fromPos = screenPositions[fromScreenId];
        const toPos = screenPositions[toScreenId];

        if (!fromPos || !toPos) return null;

        // Get offset for bidirectional connections
        const offset = getBidirectionalOffset(connection, connections);

        // Calculate centers
        const fromCenter = {
            x: fromPos.x + SCREEN_WIDTH / 2,
            y: fromPos.y + SCREEN_HEIGHT / 2,
        };
        const toCenter = {
            x: toPos.x + SCREEN_WIDTH / 2,
            y: toPos.y + SCREEN_HEIGHT / 2,
        };

        // Determine which sides to connect based on relative positions
        const dx = toCenter.x - fromCenter.x;
        const dy = toCenter.y - fromCenter.y;

        let fromPoint, toPoint;

        if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal connection
            if (dx > 0) {
                // From right to left
                fromPoint = {
                    x: fromPos.x + SCREEN_WIDTH,
                    y: fromPos.y + SCREEN_HEIGHT / 2 + offset,
                };
                toPoint = {
                    x: toPos.x,
                    y: toPos.y + SCREEN_HEIGHT / 2 + offset,
                };
            } else {
                // From left to right
                fromPoint = {
                    x: fromPos.x,
                    y: fromPos.y + SCREEN_HEIGHT / 2 + offset,
                };
                toPoint = {
                    x: toPos.x + SCREEN_WIDTH,
                    y: toPos.y + SCREEN_HEIGHT / 2 + offset,
                };
            }
        } else {
            // Vertical connection
            if (dy > 0) {
                // From bottom to top
                fromPoint = {
                    x: fromPos.x + SCREEN_WIDTH / 2 + offset,
                    y: fromPos.y + SCREEN_HEIGHT,
                };
                toPoint = {
                    x: toPos.x + SCREEN_WIDTH / 2 + offset,
                    y: toPos.y,
                };
            } else {
                // From top to bottom
                fromPoint = {
                    x: fromPos.x + SCREEN_WIDTH / 2 + offset,
                    y: fromPos.y,
                };
                toPoint = {
                    x: toPos.x + SCREEN_WIDTH / 2 + offset,
                    y: toPos.y + SCREEN_HEIGHT,
                };
            }
        }

        return { fromPoint, toPoint };
    };

    // Create curved path for connection
    const createCurvedPath = (
        fromPoint: { x: number; y: number },
        toPoint: { x: number; y: number },
    ) => {
        const dx = toPoint.x - fromPoint.x;
        const dy = toPoint.y - fromPoint.y;

        // Control points for bezier curve
        const controlOffset = Math.min(Math.abs(dx), Math.abs(dy)) * 0.5;

        let cp1, cp2;

        if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal curve
            cp1 = {
                x: fromPoint.x + (dx > 0 ? controlOffset : -controlOffset),
                y: fromPoint.y,
            };
            cp2 = {
                x: toPoint.x + (dx > 0 ? -controlOffset : controlOffset),
                y: toPoint.y,
            };
        } else {
            // Vertical curve
            cp1 = {
                x: fromPoint.x,
                y: fromPoint.y + (dy > 0 ? controlOffset : -controlOffset),
            };
            cp2 = {
                x: toPoint.x,
                y: toPoint.y + (dy > 0 ? -controlOffset : controlOffset),
            };
        }

        return [
            fromPoint.x,
            fromPoint.y,
            cp1.x,
            cp1.y,
            cp2.x,
            cp2.y,
            toPoint.x,
            toPoint.y,
        ];
    };

    // Create arrow head
    const createArrowHead = (
        fromPoint: { x: number; y: number },
        toPoint: { x: number; y: number },
    ) => {
        const angle = Math.atan2(
            toPoint.y - fromPoint.y,
            toPoint.x - fromPoint.x,
        );
        const arrowLength = 10;
        const arrowAngle = Math.PI / 6;

        const x1 = toPoint.x - arrowLength * Math.cos(angle - arrowAngle);
        const y1 = toPoint.y - arrowLength * Math.sin(angle - arrowAngle);
        const x2 = toPoint.x - arrowLength * Math.cos(angle + arrowAngle);
        const y2 = toPoint.y - arrowLength * Math.sin(angle + arrowAngle);

        return [toPoint.x, toPoint.y, x1, y1, toPoint.x, toPoint.y, x2, y2];
    };

    return (
        <Layer>
            {connections.map((connection) => {
                console.log(
                    'ConnectionLayer: Processing connection:',
                    connection,
                );
                console.log(
                    'ConnectionLayer: Screen positions:',
                    screenPositions,
                );

                const points = calculateConnectionPoints(
                    connection.fromScreenId,
                    connection.toScreenId,
                    connection,
                );

                console.log(
                    'ConnectionLayer: Calculated points for connection:',
                    connection.id,
                    points,
                );
                if (!points) {
                    console.log(
                        'ConnectionLayer: No points calculated for connection:',
                        connection.id,
                    );
                    return null;
                }

                const { fromPoint, toPoint } = points;
                const color = getConnectionColor(connection.trigger);
                const style = getConnectionStyle(connection.trigger);
                const curvedPath = createCurvedPath(fromPoint, toPoint);
                const arrowPoints = createArrowHead(fromPoint, toPoint);

                // Calculate label position (midpoint of connection)
                const labelX = (fromPoint.x + toPoint.x) / 2;
                const labelY = (fromPoint.y + toPoint.y) / 2;

                return (
                    <Group key={connection.id}>
                        {/* Connection line */}
                        <Line
                            points={curvedPath}
                            stroke={color}
                            strokeWidth={style.strokeWidth}
                            dash={style.dash}
                            bezier={true}
                            opacity={0.8}
                        />

                        {/* Arrow head */}
                        <Line
                            points={arrowPoints}
                            stroke={color}
                            strokeWidth={style.strokeWidth}
                            opacity={0.8}
                        />

                        {/* Connection label */}
                        {connection.label && (
                            <Group>
                                <Circle
                                    x={labelX}
                                    y={labelY}
                                    radius={16}
                                    fill="white"
                                    stroke={color}
                                    strokeWidth={2}
                                    shadowColor="#000000"
                                    shadowOffset={{ x: 1, y: 1 }}
                                    shadowOpacity={0.2}
                                    shadowBlur={3}
                                />
                                <Text
                                    x={labelX - 12}
                                    y={labelY - 3}
                                    text={connection.label}
                                    fontSize={9}
                                    fontFamily="Arial"
                                    fill={color}
                                    width={24}
                                    align="center"
                                    fontStyle="bold"
                                />
                            </Group>
                        )}

                        {/* Trigger type indicator */}
                        <Circle
                            x={fromPoint.x + (toPoint.x - fromPoint.x) * 0.2}
                            y={fromPoint.y + (toPoint.y - fromPoint.y) * 0.2}
                            radius={4}
                            fill={color}
                            stroke="white"
                            strokeWidth={1}
                        />
                    </Group>
                );
            })}
        </Layer>
    );
}
